<template>
  <div class="max-w-xl">
    <div>
      <template v-if="state.page === 'list'">
        <div v-if="state.isLoading" class="flex items-center justify-center h-32 p-6">
          <CircleSpinner />
        </div>
        <div v-else-if="!localizations.length" class="flex flex-col items-center w-full pb-6">
          <div class="mb-4 text-lg text-center text-gray-600">No Localizations Found!</div>
          <div class="text-sm text-center text-gray-600 w-80">
            Please create a origin localization to help position the chosen data file accurately
          </div>
        </div>
        <div v-else class="mb-5">
          <VDropdown v-model="currentLocalization" :options="localizations as unknown as Option[]" size="lg">
            <template #current-single="{ item }">
              <div class="flex flex-col overflow-x-hidden">
                <div class="mb-1 font-semibold truncate">{{ item.name }}</div>
                <div class="text-sm text-gray-600 truncate">{{ localizationToString(item) }}</div>
              </div>
            </template>
            <template #option="{ item, selected }">
              <div class="flex flex-row p-4 bg-white hover_bg-copper-50">
                <input name="localization" type="radio" :value="item.name" :checked="selected" />
                <label for="localization" class="overflow-hidden">
                  <div class="mb-1 font-semibold text-gray-700 truncate">{{ `${item.name}` }}</div>
                  <div class="text-sm text-gray-600 truncate">{{ localizationToString(item) }}</div>
                </label>
              </div>
            </template>
          </VDropdown>
        </div>
      </template>
      <DataFilesCreateEditOriginLocalization
        v-else
        :is-name-already-used="state.isNameAlreadyUsed"
        submit-btn-text="Create &amp; add to model"
        @input="onFormInput"
        @error="state.errors = $event"
        @cancel="handleTransition('list')"
        @submit="createLocalization"
      />
    </div>
    <div v-if="!state.isLoading">
      <div
        v-if="!localizations.length && state.page === 'list'"
        class="flex items-center justify-end pt-6 border-t border-gray-100 space-x-4"
      >
        <VButton variant="light" @click="$emit('update:showing')">Cancel</VButton>
        <VButton variant="copper" @click="handleTransition('form')">Create Localization</VButton>
      </div>
      <div v-else-if="state.page === 'list'" class="flex items-center justify-end pt-6 border-t border-gray-100 space-x-4">
        <div class="flex justify-between w-full">
          <VButton v-if="isInternalAdmin" variant="gold" :disabled="state.isSubmitting" @click="handleTransition('form')"
            >New Localization</VButton
          >
          <VButton class="ml-auto" variant="copper" :disabled="state.isSubmitting" @click="handleLocalizationUpdate">{{
            actionButtonText
          }}</VButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { reactive } from 'vue'

import type { DrillDataLocalization } from '~/entities/drillLabel'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { Drill } from '~/entities/drill'
import type { EditDeck } from '~/composables/useEditDeck'
import type { Option } from '@/components/VDropdown.vue'

type State = {
  isLoading?: boolean
  isSubmitting?: boolean
  localizationData: Partial<DrillDataLocalization & { errors?: any }>
  page?: 'list' | 'form'
  errors: any
  isNameAlreadyUsed: boolean
}

const props = defineProps({
  drillDataSet: {
    type: Object as PropType<Drill | null>,
    default: null,
  },
  initialDrillDataSetLayerGroup: {
    type: Object as PropType<DrillDataSetLayerGroup | null>,
    default: null,
  },
  isInternalAdmin: {
    type: Boolean,
    default: false,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  drillDataSetManager: {
    type: Object as PropType<DrillDataSetManager>,
    required: true,
  },
})

const emit = defineEmits(['update:header', 'success', 'update:showing'])

const state = reactive<State>({
  isLoading: false,
  isSubmitting: false,
  localizationData: {},
  page: 'list',
  errors: {},
  isNameAlreadyUsed: false,
})

const actionButtonText = computed(() => (props.initialDrillDataSetLayerGroup ? 'Update' : 'Add to Model'))

const { $sentry } = useNuxtApp()

const {
  localizations,
  currentLocalization,
  updateLocalization,
  localizationToString,
  createLocalization: _createLocalization,
} = useLocalizations({
  drillDataSet: props.drillDataSet,
  initialDrillDataSetLayerGroup: props.initialDrillDataSetLayerGroup,
  companyId: props.deck.company_id,
  drillDataSetManager: props.drillDataSetManager,
})

function onFormInput(localizationData: Partial<DrillDataLocalization>) {
  state.isNameAlreadyUsed = false
  state.localizationData = localizationData
}

function handleTransition(mode: 'form' | 'list') {
  state.page = mode
  emit('update:header', mode === 'form' ? 'Create New Project Localization' : 'Choose a Localization')
}

async function handleLocalizationUpdate() {
  if (!props.drillDataSetManager) return

  try {
    state.isSubmitting = true
    await updateLocalization()
  } catch (e) {
    $sentry?.captureException(e)
  } finally {
    state.isSubmitting = false

    const aiTargetNotificationPayload = props.drillDataSet?.data_type === 'ai_target' ? { title: 'Successfully imported targets!' } : null
    emit('success', aiTargetNotificationPayload)
  }
}

async function createLocalization() {
  if (!props.drillDataSetManager || Object.keys(state.errors).length) return

  if (localizations.value.find((l: DrillDataLocalization) => l.name === state.localizationData.name)) {
    state.isNameAlreadyUsed = true
    return
  }

  try {
    state.isSubmitting = true
    await _createLocalization(state.localizationData)
  } catch (e) {
    $sentry?.captureException(e)
  } finally {
    state.isSubmitting = false

    const aiTargetNotificationPayload = props.drillDataSet?.data_type === 'ai_target' ? { title: 'Successfully imported targets!' } : null
    emit('success', aiTargetNotificationPayload)
  }
}
</script>

<style lang="stylus" scoped>
input[type='radio']
	@apply mr-4 mt-1 flex self-start appearance-none h-4 w-4 rounded-full border border-gray-300 flex-shrink-0

[type=radio]:checked
	accent-color: rgba(63, 128, 140, 1);
	@apply bg-copper-700
	background-image url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='3'/%3e%3c/svg%3e")
</style>
